/**
 * The English message catalogue — the single source of every user-facing
 * dashboard string that has been routed through `t()`.
 *
 * English is the default AND the only shipped locale. This file exists to make
 * translation *possible* later (see `web/docs/i18n.md`), not to imply it has
 * happened: nothing here is machine-translated, and the rule for the day a
 * second catalogue is added is written into that note — legal, certificate and
 * verdict text is counsel-reviewed per jurisdiction and never auto-translated.
 *
 * Keys are dotted by surface (`evidence.preview.*`). The value is the finished
 * English sentence; `{name}` placeholders are filled by `t(key, params)`. Add a
 * key here before referencing it — a missing key renders as the key itself
 * rather than crashing, which is a visible defect, not a silent one.
 */
export const en = {
  // ---- Evidence module chrome ---------------------------------------------
  'evidence.title': 'Evidence',
  // INTENT COPY, not a certification claim. "Court-grade" names the ambition of
  // the module (evidence built to survive a courtroom), NOT an accreditation the
  // product holds. Keep it a plain subtitle: do not extend it into "certified",
  // "court-admissible", "guaranteed", or any wording that asserts a third party
  // has attested the product — that would be a claim the platform cannot back
  // (see the honest-verdict rule; the verifier caps at STRUCTURE_VERIFIED).
  'evidence.subtitle': 'Court-grade evidence management',

  // ---- Asset detail --------------------------------------------------------
  'evidence.asset.back': '‹ All evidence',
  'evidence.asset.record': 'Evidence record',

  // ---- Preview -------------------------------------------------------------
  'evidence.preview.title': 'Preview',
  // The honesty line. It must never read as though the preview is the evidence,
  // or as though rendering it alters the original bytes.
  'evidence.preview.label':
    'This is a rendering of the original media, shown here only so a person can look at it. The verified evidence is the original itself — the bytes behind the content hash above — and nothing on this screen changes it.',
  'evidence.preview.imageAlt': 'Rendering of the evidence media, shown for viewing',
  'evidence.preview.videoLabel': 'Rendering of the evidence media, shown for viewing',
  'evidence.preview.loading': 'Loading the original bytes',
  'evidence.preview.downloadOriginal': 'Download original',
  'evidence.preview.error':
    'The preview could not be loaded. The verified original is unaffected — download it to view.',
  // Fallback states — a placeholder plus a way to get the real bytes, never a
  // broken element.
  'evidence.preview.fallback.tooLarge':
    'This recording is larger than the {limit} inline-preview limit, so it is not rendered here.',
  'evidence.preview.fallback.unsupported':
    'This media type ({mediaType}) is not one the browser previews inline.',
  'evidence.preview.fallback.download':
    'Download the original to view it — that file is the verified evidence.',

  // ---- Verification block --------------------------------------------------
  'evidence.verification.title': 'Verification',

  // ==========================================================================
  // Evidence WORKSPACE surfaces (cases / disclosure / redaction / onboarding).
  // English-only, same as above; nothing here is machine-translated. Legal,
  // certificate and verdict text is NOT routed — it stays inline, counsel-
  // reviewed per jurisdiction (see web/docs/i18n.md and the disclosure screen's
  // VERDICT_NOTE / rendered certificate).
  // ==========================================================================

  // ---- Shared workspace chrome: the seam gate + reason-for-access prompt ----
  'reason.title': 'Reason for access',
  'reason.intro':
    'Evidence records are sensitive. State why you are accessing them — the reason is recorded in the audit trail alongside your access, and auditors read it back.',
  'reason.field.reason': 'Reason',
  'reason.field.detail': 'Detail (optional)',
  'reason.field.detail.placeholder': 'Case reference, request id…',
  'reason.field.detail.hint': 'Shown to auditors next to your access record.',
  'reason.continue': 'Continue',
  'gate.unconfigured':
    'Evidence management is not switched on in this environment yet. Nothing is wrong — these workspace surfaces appear the moment the evidence service is connected.',
  'gate.notEnabled.title': 'Evidence management is off for this organisation',
  'gate.notEnabled.body':
    'Cases, disclosure and redaction are part of the evidence workspace. Turn evidence management on from the Evidence tab — an owner or admin can do it in one step — and these surfaces open with it.',
  'gate.notEnabled.cta': 'Go to Evidence',
  'gate.forbidden':
    'Your role in this organisation has no standing to work with evidence here. An owner or admin can adjust roles under People.',
  'gate.error.title': 'This isn’t loading',
  'gate.error.body':
    'The evidence service didn’t answer. Nothing has been lost — the record is held by the evidence service, not this dashboard. Try again in a moment; if it keeps failing, an owner or admin can check the evidence service’s status.',

  // ---- Shared small strings ------------------------------------------------
  'common.loading': 'Loading…',
  'common.notFound': 'Not found',
  'common.chooseAsset': 'Choose an asset…',
  'common.committedEvidence': 'Committed evidence',
  'common.loadingCommitted': 'Loading committed evidence…',

  // ---- Cases ---------------------------------------------------------------
  'cases.title': 'Cases',
  'cases.sub': 'Group evidence for a matter',
  'cases.metric.cases': 'Cases',
  'cases.metric.open': 'Open',
  'cases.new.title': 'New case',
  'cases.new.field.title': 'Title',
  'cases.new.field.ref': 'External reference (optional)',
  'cases.new.create': 'Create case',
  'cases.new.creating': 'Creating…',
  'cases.list.title': 'Cases',
  'cases.list.empty':
    'No cases yet. A case groups evidence assets for one matter — its record of who linked what, and when, is kept in each asset’s chain of custody.',
  'cases.head.title': 'Title',
  'cases.head.reference': 'Reference',
  'cases.head.state': 'State',
  'cases.head.assets': 'Assets',
  'cases.head.created': 'Created',
  'cases.open': 'Open',
  'cases.back': '‹ All cases',
  'cases.link.title': 'Link an asset',
  'cases.link.none': 'No committed evidence is available to link that isn’t already on this case.',
  'cases.link.cta': 'Link',
  'cases.link.linking': 'Linking…',
  'cases.linked.title': 'Linked evidence',
  'cases.linked.empty': 'Nothing is linked to this case yet.',
  'cases.linked.head.captured': 'Captured',
  'cases.linked.head.state': 'State',
  'cases.linked.head.type': 'Type',
  'cases.linked.head.hash': 'Content hash',
  'cases.linked.head.linked': 'Linked',
  'cases.unlink': 'Unlink',

  // ---- Disclosure ----------------------------------------------------------
  'disclosure.title': 'Disclosure',
  'disclosure.sub': 'Court-ready sealed bundles',
  'disclosure.metric.disclosures': 'Disclosures',
  'disclosure.metric.active': 'Active',
  'disclosure.list.title': 'Disclosures',
  'disclosure.list.empty':
    'Nothing has been disclosed yet. A disclosure records who received which evidence, for what purpose, and by what method — and produces a sealed bundle anyone can verify offline.',
  'disclosure.head.recipient': 'Recipient',
  'disclosure.head.purpose': 'Purpose',
  'disclosure.head.method': 'Method',
  'disclosure.head.items': 'Items',
  'disclosure.head.created': 'Created',
  'disclosure.head.state': 'State',
  'disclosure.open': 'Open',
  'disclosure.back': '‹ All disclosures',
  'disclosure.new.title': 'Disclose evidence',
  'disclosure.new.field.recipient': 'Recipient',
  'disclosure.new.field.purpose': 'Purpose',
  'disclosure.new.field.method': 'Method',
  'disclosure.new.field.what': 'What to disclose',
  'disclosure.new.field.case': 'Case',
  'disclosure.new.field.expires': 'Expires (optional)',
  'disclosure.new.seal': 'Seal disclosure',
  'disclosure.new.sealing': 'Sealing…',
  'disclosure.items.title': 'Items disclosed',
  'disclosure.items.head.asset': 'Asset',
  'disclosure.items.head.contentRef': 'Content reference',
  'disclosure.items.head.derivativeOf': 'Derivative of',
  'disclosure.bundle.title': 'Sealed bundle',
  'disclosure.bundle.build': 'Build bundle',
  'disclosure.bundle.rebuild': 'Rebuild bundle',
  'disclosure.bundle.building': 'Building…',
  'disclosure.bundle.download': 'Download bundle JSON',
  'disclosure.revoke.title': 'Revoke',
  'disclosure.revoke.cta': 'Revoke disclosure',
  'disclosure.revoke.confirm': 'Revoke this disclosure',
  'disclosure.revoke.revoking': 'Revoking…',
  'disclosure.revoke.keep': 'Keep it',

  // ---- Redaction -----------------------------------------------------------
  'redaction.title': 'Redaction',
  'redaction.sub': 'Create a redaction derivative',
  'redaction.noRole':
    'Redaction is a custodian or redactor action. Your role in this organisation cannot create redaction derivatives — an owner or admin can adjust roles under People.',
  'redaction.noEvidence':
    'There is no committed evidence to redact yet. A redaction derivative is made from a stored asset; the moment one is committed it becomes available here.',
  'redaction.form.title': 'Redact an asset',
  'redaction.regions.label': 'Regions',
  'redaction.regions.addRegion': 'Add region',
  'redaction.regions.removeRegion': 'Remove region',
  'redaction.create': 'Create redaction derivative',
  'redaction.creating': 'Creating derivative…',
  'redaction.result.title': 'Redaction derivative',

  // ---- Onboarding (first-run wizard) ---------------------------------------
  'onboarding.title': 'Set up evidence management',
  'onboarding.skip': 'Skip for now',
  'onboarding.skip.label': 'Skip evidence setup for now',
  'onboarding.status.done': 'Done',
  'onboarding.status.now': 'Now',
  'onboarding.status.next': 'Next',
  'onboarding.step.enable': 'Turn on evidence management',
  'onboarding.step.enable.cta': 'Turn on evidence management',
  'onboarding.step.enable.busy': 'Turning on…',
  'onboarding.step.ingest': 'Ingest your first asset',
  'onboarding.step.ingest.cta': 'Record and ingest',
  'onboarding.step.ingest.demo': 'Load sample data instead',
  'onboarding.step.verify': 'Hand someone a verification bundle',
  'onboarding.step.verify.cta': 'Open your latest asset',
  'onboarding.step.verify.ack': 'Mark this step done',
} as const satisfies Record<string, string>;

export type MessageKey = keyof typeof en;
